import FemaleIcon from "@mui/icons-material/Female";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import MaleIcon from "@mui/icons-material/Male";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useMemo } from "react";
import {
  calculateAnnouncedPlace,
  formatCategoryRecordsLevelsTooltip,
  getNiveau,
  getTotalAnnonced,
  getTotalReal,
} from "../../../util/src/index";
import dico from "../../../util/src/language/dico";
import {
  AthletesModel,
  CompetitionFormat,
  NiveauxModel,
  RecordModel,
  ThemeColors,
} from "../../../util/src/types";
import RenderRecord from "../misc/RenderRecord";
import LiftCell from "./LiftCell";
import { getMaxLift } from "./hooks";
import { TableColumn } from "./types";

/**
 * Small stopPropagation button to open hypothetical Powerscout ranking.
 */
const HypotheticalRankButton = ({
  athlete,
  field,
  value,
  onHypotheticalRankClick,
}: {
  athlete: AthletesModel;
  field: string;
  value: number;
  onHypotheticalRankClick?: (payload: {
    athlete: AthletesModel;
    field: string;
    value: number;
  }) => void;
}) => {
  if (!onHypotheticalRankClick || !(value > 0)) {
    return null;
  }
  return (
    <Tooltip title={dico().powerscout.hypotheticalRankTooltip}>
      <IconButton
        size="small"
        aria-label={dico().powerscout.hypotheticalRankTooltip}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onHypotheticalRankClick({ athlete, field, value });
        }}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          p: 0.15,
          ml: 0.25,
          color: "text.secondary",
          "& .MuiSvgIcon-root": { fontSize: "0.85rem" },
        }}
      >
        <LeaderboardOutlinedIcon />
      </IconButton>
    </Tooltip>
  );
};

/**
 * Hook for generating table columns configuration based on current lift type and competition format
 */
export const useTableColumns = (
  currentLiftType: "squat" | "bench" | "deadlift",
  records: RecordModel[],
  colors: ThemeColors,
  competitionFormat?: CompetitionFormat,
  levels?: NiveauxModel[],
  athletes?: AthletesModel[],
  onHypotheticalRankClick?: (payload: {
    athlete: AthletesModel;
    field: string;
    value: number;
  }) => void,
  defaultLevelName?: string | null,
  /** Meet scoring formula - drives GL Points / DOTS column headers. */
  pointsFormula?: string | null,
  isNational = false,
): TableColumn[] => {
  return useMemo(() => {
    // Width from longest "weight + age" label (e.g. "105 Kg OPEN") so EN/FR content is not clipped
    const categoryHeader = dico().category;
    const categoryLabels = (athletes ?? []).map((a) =>
      `${a.catPoid?.nom ?? ""} ${a.catAge?.code ?? ""}`.trim(),
    );
    const longestCategoryLen = Math.max(
      categoryHeader.length,
      8,
      ...categoryLabels.map((label) => label.length),
    );
    const categoryMinWidth = Math.min(
      220,
      Math.max(100, Math.round(longestCategoryLen * 9 + 24)),
    );
    // Base columns that are always shown
    const baseColumns: TableColumn[] = [
      {
        id: "athlete",
        header: dico().ahleteDisplay,
        flex: 2,
        frozen: true,
        align: "left",
        render: (athlete: AthletesModel) => (
          <Typography
            sx={{
              fontSize: {
                xs: "0.7rem",
                sm: "0.8rem",
                md: "0.9rem",
                lg: "1.2rem",
              },
              fontWeight: "medium",
            }}
          >
            {athlete.prenom} {athlete.nom}
          </Typography>
        ),
      },
      {
        id: "club",
        header: dico().athletes.club,
        flex: 3,
        minWidth: 120,
        align: "left",
        render: (athlete: AthletesModel) => (
          <Typography
            sx={{
              fontSize: {
                xs: "0.6rem",
                sm: "0.7rem",
                md: "0.8rem",
                lg: "1.2rem",
              },
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {athlete.club}
          </Typography>
        ),
      },
      {
        id: "sexe",
        header: dico().athletes.sexe,
        flex: 1,
        align: "center",
        render: (athlete: AthletesModel) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {athlete.sexe === "M" ? (
              <MaleIcon
                sx={{
                  color: "primary.main",
                  fontSize: {
                    xs: "0.8rem",
                    sm: "0.9rem",
                    md: "1rem",
                    lg: "1.5rem",
                  },
                }}
              />
            ) : (
              <FemaleIcon
                sx={{
                  color: "secondary.main",
                  fontSize: {
                    xs: "1.2rem",
                    sm: "0.9rem",
                    md: "1rem",
                    lg: "1.5rem",
                  },
                }}
              />
            )}
          </Box>
        ),
      },
      {
        id: "pdc",
        header: dico().athletes.pdc,
        flex: 0.8,
        align: "center",
        render: (athlete: AthletesModel) => (
          <Typography
            sx={{
              fontSize: {
                xs: "0.7rem",
                sm: "0.8rem",
                md: "0.9rem",
                lg: "1.2rem",
              },
              textAlign: "center",
            }}
          >
            {athlete.pdc}
          </Typography>
        ),
      },
      {
        id: "lot",
        header: dico().athletes.lot,
        flex: 0.6,
        align: "center",
        render: (athlete: AthletesModel) => (
          <Typography
            sx={{
              fontSize: {
                xs: "0.7rem",
                sm: "0.8rem",
                md: "0.9rem",
                lg: "1.2rem",
              },
              textAlign: "center",
            }}
          >
            {athlete.lot}
          </Typography>
        ),
      },
      {
        id: "catPoidsAge",
        header: categoryHeader,
        flex: 1.5,
        minWidth: categoryMinWidth,
        align: "center",
        render: (athlete: AthletesModel) => {
          const tooltipContent = formatCategoryRecordsLevelsTooltip(
            athlete,
            records,
            levels ?? [],
          );
          const categoryLabel = (
            <Typography
              sx={{
                fontSize: {
                  xs: "0.7rem",
                  sm: "0.8rem",
                  md: "0.9rem",
                  lg: "1.2rem",
                },
                textAlign: "center",
                whiteSpace: "nowrap",
                cursor: tooltipContent ? "help" : "inherit",
              }}
            >
              {athlete.catPoid?.nom} {athlete.catAge?.code}
            </Typography>
          );
          if (!tooltipContent) {
            return categoryLabel;
          }
          return (
            <Tooltip
              title={
                <Box sx={{ whiteSpace: "pre-line", fontSize: "0.7rem" }}>
                  {tooltipContent}
                </Box>
              }
            >
              <span>{categoryLabel}</span>
            </Tooltip>
          );
        },
      },
      {
        id: "discipline",
        header: dico().discipline,
        flex: 1.5,
        align: "center",
        render: (athlete: AthletesModel) => (
          <Typography
            sx={{
              fontSize: {
                xs: "0.7rem",
                sm: "0.8rem",
                md: "0.9rem",
                lg: "1.2rem",
              },
              textAlign: "center",
            }}
          >
            {athlete.discipline?.code}
          </Typography>
        ),
      },
    ];

    // Squat columns
    const squatColumns: TableColumn[] =
      currentLiftType === "squat"
        ? [
            {
              id: "squat1",
              header: `${dico().sq} 1`,
              flex: 1,
              align: "center",
              render: (athlete: AthletesModel) => (
                <LiftCell
                  athlete={athlete}
                  field="squat1"
                  records={records}
                  onHypotheticalRankClick={onHypotheticalRankClick}
                />
              ),
            },
            {
              id: "squat2",
              header: `${dico().sq} 2`,
              flex: 1,
              align: "center",
              render: (athlete: AthletesModel) => (
                <LiftCell
                  athlete={athlete}
                  field="squat2"
                  records={records}
                  onHypotheticalRankClick={onHypotheticalRankClick}
                />
              ),
            },
            {
              id: "squat3",
              header: `${dico().sq} 3`,
              flex: 1,
              align: "center",
              render: (athlete: AthletesModel) => (
                <LiftCell
                  athlete={athlete}
                  field="squat3"
                  records={records}
                  onHypotheticalRankClick={onHypotheticalRankClick}
                />
              ),
            },
          ]
        : [
            {
              id: "squatMax",
              header: dico().squat,
              flex: 1.5,
              align: "center",
              render: (athlete: AthletesModel) => {
                const maxLift = getMaxLift(athlete, "squat");
                return (
                  <LiftCell
                    athlete={athlete}
                    field={maxLift.field}
                    records={records}
                    isMax={true}
                    onHypotheticalRankClick={onHypotheticalRankClick}
                  />
                );
              },
            },
          ];

    // Bench columns - always expanded on SINGLE_LIFT (DC/BP, only movement)
    const expandBenchColumns =
      currentLiftType === "bench" ||
      competitionFormat === CompetitionFormat.SINGLE_LIFT;
    const benchColumns: TableColumn[] = expandBenchColumns
      ? [
          {
            id: "bench1",
            header: `${dico().dc} 1`,
            flex: 1,
            align: "center",
            render: (athlete: AthletesModel) => (
              <LiftCell
                athlete={athlete}
                field="bench1"
                records={records}
                onHypotheticalRankClick={onHypotheticalRankClick}
              />
            ),
          },
          {
            id: "bench2",
            header: `${dico().dc} 2`,
            flex: 1,
            align: "center",
            render: (athlete: AthletesModel) => (
              <LiftCell
                athlete={athlete}
                field="bench2"
                records={records}
                onHypotheticalRankClick={onHypotheticalRankClick}
              />
            ),
          },
          {
            id: "bench3",
            header: `${dico().dc} 3`,
            flex: 1,
            align: "center",
            render: (athlete: AthletesModel) => (
              <LiftCell
                athlete={athlete}
                field="bench3"
                records={records}
                onHypotheticalRankClick={onHypotheticalRankClick}
              />
            ),
          },
        ]
      : [
          {
            id: "benchMax",
            header: dico().bench,
            flex: 1.5,
            align: "center",
            render: (athlete: AthletesModel) => {
              const maxLift = getMaxLift(athlete, "bench");
              return (
                <LiftCell
                  athlete={athlete}
                  field={maxLift.field}
                  records={records}
                  isMax={true}
                  onHypotheticalRankClick={onHypotheticalRankClick}
                />
              );
            },
          },
        ];

    // Deadlift columns
    const deadliftColumns: TableColumn[] =
      currentLiftType === "deadlift"
        ? [
            {
              id: "deadlift1",
              header: `${dico().sdt} 1`,
              flex: 1,
              align: "center",
              render: (athlete: AthletesModel) => (
                <LiftCell
                  athlete={athlete}
                  field="deadlift1"
                  records={records}
                  onHypotheticalRankClick={onHypotheticalRankClick}
                />
              ),
            },
            {
              id: "deadlift2",
              header: `${dico().sdt} 2`,
              flex: 1,
              align: "center",
              render: (athlete: AthletesModel) => (
                <LiftCell
                  athlete={athlete}
                  field="deadlift2"
                  records={records}
                  onHypotheticalRankClick={onHypotheticalRankClick}
                />
              ),
            },
            {
              id: "deadlift3",
              header: `${dico().sdt} 3`,
              flex: 1,
              align: "center",
              render: (athlete: AthletesModel) => (
                <LiftCell
                  athlete={athlete}
                  field="deadlift3"
                  records={records}
                  onHypotheticalRankClick={onHypotheticalRankClick}
                />
              ),
            },
          ]
        : [
            {
              id: "deadliftMax",
              header: dico().deadlift,
              flex: 1.5,
              align: "center",
              render: (athlete: AthletesModel) => {
                const maxLift = getMaxLift(athlete, "deadlift");
                return (
                  <LiftCell
                    athlete={athlete}
                    field={maxLift.field}
                    records={records}
                    isMax={true}
                    onHypotheticalRankClick={onHypotheticalRankClick}
                  />
                );
              },
            },
          ];

    // Combine lift columns based on competition format
    const liftColumns: TableColumn[] =
      competitionFormat === CompetitionFormat.SINGLE_LIFT
        ? [...benchColumns]
        : [...squatColumns, ...benchColumns, ...deadliftColumns];
    // Final columns (totals and points)
    const finalColumns: TableColumn[] = [
      // Total Annoncé column - hidden for singleLift competitions
      ...(competitionFormat !== CompetitionFormat.SINGLE_LIFT
        ? [
            {
              id: "totalAnnonce",
              header: dico().athletes.totalAnnonce,
              flex: 1.6,
              minWidth: 110,
              align: "center" as const,
              render: (athlete: AthletesModel) => {
                const total = Number(getTotalAnnonced(athlete) ?? 0);
                return (
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                      margin: "-8px -4px",
                      padding: "8px 4px",
                      borderLeft: "1px solid black",
                      gap: "2px",
                    }}
                  >
                    <RenderRecord
                      athlete={athlete}
                      lift="Total"
                      total={true}
                      weight={total}
                      records={records}
                    />
                    <Typography
                      sx={{
                        fontSize: {
                          xs: "0.7rem",
                          sm: "0.8rem",
                          md: "0.9rem",
                          lg: "1.2rem",
                        },
                        fontWeight: "bold",
                        whiteSpace: "normal",
                        textAlign: "center",
                      }}
                    >
                      {total || ""}
                    </Typography>
                    <HypotheticalRankButton
                      athlete={athlete}
                      field="totalAnnonce"
                      value={total}
                      onHypotheticalRankClick={onHypotheticalRankClick}
                    />
                  </Box>
                );
              },
            },
          ]
        : []),
      {
        id: "total",
        header: dico().athletes.totalkg,
        flex: 1.5,
        minWidth: 90,
        align: "center",
        render: (athlete: AthletesModel) => {
          const total = Number(getTotalReal(athlete) ?? 0);
          return (
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                margin: "-8px -4px",
                padding: "8px 4px",
                gap: "2px",
              }}
            >
              <RenderRecord
                athlete={athlete}
                lift="total"
                total={true}
                weight={total}
                records={records}
              />
              <Typography
                sx={{
                  fontSize: {
                    xs: "0.7rem",
                    sm: "0.8rem",
                    md: "0.9rem",
                    lg: "1.2rem",
                  },
                  fontWeight: "bold",
                  color: colors.primary,
                  whiteSpace: "normal",
                  textAlign: "center",
                }}
              >
                {total || ""}
              </Typography>
              <HypotheticalRankButton
                athlete={athlete}
                field="total"
                value={total}
                onHypotheticalRankClick={onHypotheticalRankClick}
              />
            </Box>
          );
        },
      },
      // Points Annoncés column - hidden for singleLift competitions
      ...(competitionFormat !== CompetitionFormat.SINGLE_LIFT
        ? [
            {
              id: "pointsAnnonced",
              header: dico().athletes.pointsAnnonced(
                pointsFormula ?? undefined,
              ),
              // EN: "GL Points Announced" / "DOTS Announced"
              flex: 1.8,
              minWidth: 130,
              align: "center" as const,
              render: (athlete: AthletesModel) => {
                const totalAnnonced = getTotalAnnonced(athlete);
                return (
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "0.7rem",
                        sm: "0.8rem",
                        md: "0.9rem",
                        lg: "1.2rem",
                      },
                      whiteSpace: "normal",
                      textAlign: "center",
                    }}
                  >
                    {Number(
                      (Number(athlete.indice) * totalAnnonced).toFixed(2),
                    )}
                  </Typography>
                );
              },
            },
          ]
        : []),
      {
        id: "points",
        header: dico().athletes.points(pointsFormula ?? undefined),
        // EN: "GL Points" / "DOTS"
        flex: 1.4,
        minWidth: 95,
        align: "center",
        render: (athlete: AthletesModel) => (
          <Typography
            sx={{
              fontSize: {
                xs: "0.7rem",
                sm: "0.8rem",
                md: "0.9rem",
                lg: "1.2rem",
              },
              whiteSpace: "normal",
              textAlign: "center",
            }}
          >
            {Number(
              (Number(athlete.indice) * Number(athlete.totalkg)).toFixed(2),
            )}
          </Typography>
        ),
      },
      ...(competitionFormat !== CompetitionFormat.SINGLE_LIFT &&
      levels &&
      levels.length > 0
        ? [
            {
              id: "levelAnnonced",
              header: dico().athletes.niveauAnnonced,
              // EN: "Level Annonced"
              flex: 1.5,
              minWidth: 110,
              align: "center" as const,
              render: (athlete: AthletesModel) => {
                const totalAnnonced = getTotalAnnonced(athlete);
                const level = getNiveau(
                  Number(totalAnnonced ?? 0),
                  athlete.catAgeId,
                  athlete.catPoidId,
                  levels,
                  athlete.disciplineId,
                  defaultLevelName,
                );
                return (
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "0.7rem",
                        sm: "0.8rem",
                        md: "0.9rem",
                        lg: "1.2rem",
                      },
                      whiteSpace: "normal",
                      textAlign: "center",
                    }}
                  >
                    {level.nom}
                  </Typography>
                );
              },
            },
          ]
        : []),
      ...(levels && levels.length > 0
        ? [
            {
              id: "level",
              header: dico().athletes.niveau,
              flex: 1.2,
              minWidth: 80,
              align: "center" as const,
              render: (athlete: AthletesModel) => {
                const total = athlete.totalkg;
                const level = getNiveau(
                  Number(total ?? 0),
                  athlete.catAgeId,
                  athlete.catPoidId,
                  levels,
                  athlete.disciplineId,
                  defaultLevelName,
                );
                return (
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "0.7rem",
                        sm: "0.8rem",
                        md: "0.9rem",
                        lg: "1.2rem",
                      },
                      whiteSpace: "normal",
                      textAlign: "center",
                    }}
                  >
                    {level.nom}
                  </Typography>
                );
              },
            },
          ]
        : []),
      {
        id: "placement",
        header: dico().ranking.placement,
        // EN: "Placement"
        flex: 1.3,
        minWidth: 95,
        align: "center",
        render: (athlete: AthletesModel) => {
          const placement = athlete.isDisqualified
            ? dico().athletes.hm
            : athlete.placement
              ? athlete.placement
              : "X";

          return (
            <Typography
              sx={{
                fontSize: {
                  xs: "0.8rem",
                  sm: "0.8rem",
                  md: "0.9rem",
                  lg: "1.2rem",
                },
                whiteSpace: "normal",
                textAlign: "center",
                fontWeight: "bold",
              }}
            >
              {placement}
            </Typography>
          );
        },
      },
      ...(athletes && athletes.length > 0
        ? [
            {
              id: "placementAnnonced",
              header: dico().ranking.placementAnnonced,
              // EN: "Placement Annonced"
              flex: 1.8,
              minWidth: 130,
              align: "center" as const,
              render: (athlete: AthletesModel) => {
                const announcedPlace = calculateAnnouncedPlace(
                  athlete,
                  athletes,
                  isNational,
                );
                const placement = athlete.isDisqualified
                  ? dico().athletes.hm
                  : announcedPlace
                    ? announcedPlace
                    : "X";
                return (
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "0.8rem",
                        sm: "0.8rem",
                        md: "0.9rem",
                        lg: "1.2rem",
                      },
                      whiteSpace: "normal",
                      textAlign: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {placement}
                  </Typography>
                );
              },
            },
          ]
        : []),
    ];

    // Return columns based on competition format
    return [...baseColumns, ...liftColumns, ...finalColumns];
  }, [
    currentLiftType,
    competitionFormat,
    levels,
    records,
    colors.primary,
    athletes,
    onHypotheticalRankClick,
    defaultLevelName,
    pointsFormula,
    isNational,
  ]);
};
